/**
 * ZAGX v2 — the one list of things that must not leave the device.
 *
 * WHY THIS FILE EXISTS
 * "Never sync secrets" was already the rule (audit M-10) and it was already implemented — in
 * two of the three places that serialise the state. cloudSave stripped the keys before the
 * upsert, cloudSnapshot stripped them before the snapshot, and `doBackup` — the button the
 * owner actually presses — wrote `JSON.stringify(S, null, 2)` with everything in it. That file
 * is what ended up committed to a repository, twenty-six times, with two live API keys in it.
 *
 * The rule was never wrong. It was copied into two call sites and missed at the third, which
 * is the same failure this codebase has now hit four separate times in one day: a rule with N
 * copies has N chances to be incomplete, and the incomplete one is the one nobody looks at.
 * So there is exactly one list here and three callers that use it.
 *
 * THE OTHER HALF, WHICH IS EASY TO MISS
 * Stripping on the way out creates a hole on the way back in. `importData` did
 * `S.settings = parsed.settings`, a wholesale replace — so restoring a (correctly) key-less
 * backup would silently delete the key the user had configured, and the next AI call would
 * fail with "AI not configured" for no visible reason. Strip on export, PRESERVE on import.
 * Neither half is safe alone.
 *
 * NO IMPORTS, BY DESIGN. This is a list and two pure functions. It is consumed by
 * persistence.js and settings.js, both of which import plenty; if this file ever needs one of
 * them, the list has stopped being a list.
 */

/**
 * Dotted paths that are device-local. A value here is written by the owner into this browser
 * and has no business in a cloud row, a snapshot, or a file that leaves the machine.
 *
 * Adding a path here is enough — every serialiser reads this list.
 */
export const SECRET_PATHS = [
  'settings.tdApiKey',      // Twelve Data price feed
  'settings.ai.apiKey',     // Anthropic / OpenAI
  'settings.mongoUri',      // never used client-side; present in older states
];

/**
 * Query-string parameter names that carry a credential.
 *
 * ⚠ SEC-002. `SECRET_PATHS` above keeps keys out of anything this app SERIALISES. It says
 * nothing about the keys this app puts in a URL — and `prices.js` sends the Twelve Data key as
 * `?...&apikey=<key>`, twice (the batch fetch and the Settings key test).
 *
 * That URL is not private. `sentry.js` enables `breadcrumbsIntegration({ fetch: true })` with
 * `maxBreadcrumbs: 30` and NO `beforeBreadcrumb` hook, so every fetch URL of the last thirty
 * events is attached to any error that gets reported — **and the key rides off the device with
 * it, to a third party, on an event the owner never sees.**
 *
 * Redaction is by PARAMETER NAME rather than by value, deliberately: this file may not import
 * anything (see the header), so it cannot read `S` to learn the current key — and a name-based
 * rule also covers a key that is rotated, a key this list has never heard of, and the next
 * integration someone adds.
 */
export const SECRET_QUERY_PARAMS = /^(api_?key|apikey|token|access_?token|secret|password|passwd|auth|sig|signature|session)$/i;

/**
 * A URL with every credential-bearing query parameter replaced by `REDACTED`.
 *
 * Returns the input unchanged when it is not a parseable URL — a breadcrumb is not worth
 * throwing over, and a scrubber that can crash the error reporter is worse than the leak.
 */
export function scrubUrl(url) {
  if (typeof url !== 'string' || !url) return url;
  try {
    const u = new URL(url, 'https://x.invalid');
    let touched = false;
    u.searchParams.forEach((_v, k) => { if (SECRET_QUERY_PARAMS.test(k)) touched = true; });
    if (!touched) return url;
    [...u.searchParams.keys()].forEach(k => {
      if (SECRET_QUERY_PARAMS.test(k)) u.searchParams.set(k, 'REDACTED');
    });
    // Preserve the original form: relative in, relative out.
    return /^[a-z][a-z0-9+.-]*:/i.test(url) ? u.toString() : u.pathname + u.search + u.hash;
  } catch {
    return url;
  }
}

function _get(obj, path) {
  return path.split('.').reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);
}
function _del(obj, path) {
  const parts = path.split('.');
  const last = parts.pop();
  const parent = parts.reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);
  if (parent && typeof parent === 'object' && last in parent) { delete parent[last]; return true; }
  return false;
}
function _set(obj, path, val) {
  const parts = path.split('.');
  const last = parts.pop();
  let cur = obj;
  for (const k of parts) {
    if (!cur[k] || typeof cur[k] !== 'object') cur[k] = {};
    cur = cur[k];
  }
  cur[last] = val;
}

/**
 * A copy of `state` with every secret removed.
 *
 * Deep-copies via JSON so the caller cannot accidentally mutate the live state — the previous
 * inline versions destructured, which is shallow, and one of them was one nested level away
 * from leaking anyway.
 *
 * @returns {{state: Object, removed: string[]}} — `removed` names what was actually present,
 *          so a caller can say so rather than claiming a strip it did not perform.
 */
export function stripSecrets(state) {
  let copy;
  try { copy = JSON.parse(JSON.stringify(state)); }
  catch (_e) { return { state: state, removed: [] }; }   // uncloneable: return as-is, claim nothing
  const removed = [];
  SECRET_PATHS.forEach(p => { if (_del(copy, p)) removed.push(p); });
  return { state: copy, removed };
}

/**
 * Carry the local secrets across an import.
 *
 * `incoming` is a backup file, which by the rule above contains no secrets. Copying it over
 * `current` wholesale would delete what the owner configured on this device. This puts back
 * every secret the incoming file does not itself carry — an incoming value, if one is somehow
 * present, wins, because a user restoring a file that has a key in it meant to restore it.
 */
export function preserveSecrets(current, incoming) {
  SECRET_PATHS.forEach(p => {
    const inc = _get(incoming, p);
    if (inc !== undefined && inc !== '') return;    // the file has one: leave it alone
    const mine = _get(current, p);
    if (mine !== undefined && mine !== '') _set(incoming, p, mine);
  });
  return incoming;
}

/** True when the object carries any secret — used to assert a payload before it is sent. */
export function hasSecrets(obj) {
  return SECRET_PATHS.some(p => {
    const v = _get(obj, p);
    return v !== undefined && v !== null && v !== '';
  });
}
